<script setup>
import { computed, ref } from 'vue';
import { formatNumber, seriesMax } from './metricsHelpers';

const props = defineProps({
  dates: { type: Array, required: true },
  gained: { type: Array, required: true },
  lost: { type: Array, required: true },
  gainedLabel: { type: String, required: true },
  lostLabel: { type: String, required: true },
  formatDate: { type: Function, default: date => date },
});

const W = 640;
const H = 180;
const PAD = { top: 12, right: 12, bottom: 24, left: 48 };
const hovered = ref(null);

// Ganho sobe (teal), perda desce (iris): a direção diz o que é, a cor reforça.
const limit = computed(() =>
  Math.max(1, seriesMax(props.gained), seriesMax(props.lost))
);
const mid = (PAD.top + H - PAD.bottom) / 2;
const half = (H - PAD.top - PAD.bottom) / 2;
const slot = computed(
  () => (W - PAD.left - PAD.right) / Math.max(1, props.dates.length)
);
const barWidth = computed(() => Math.max(2, slot.value - 2));
const xAt = index => PAD.left + index * slot.value + 1;
const height = value =>
  typeof value === 'number' ? (value / limit.value) * half : 0;

const gainedBars = computed(() =>
  props.gained
    .map((value, index) => ({ index, value }))
    .filter(bar => typeof bar.value === 'number' && bar.value > 0)
);
const lostBars = computed(() =>
  props.lost
    .map((value, index) => ({ index, value }))
    .filter(bar => typeof bar.value === 'number' && bar.value > 0)
);

const select = index => {
  hovered.value = index;
};
const onMove = event => {
  const rect = event.currentTarget.getBoundingClientRect();
  const svgX = ((event.clientX - rect.left) / rect.width) * W;
  const index = Math.floor((svgX - PAD.left) / slot.value);
  select(index >= 0 && index < props.dates.length ? index : null);
};

// Sem mouse, a seta parte do último dia (o mais recente) e anda a partir dali.
const moveSelection = delta => {
  const n = props.dates.length;
  if (!n) return;
  const base = hovered.value === null ? n - 1 : hovered.value;
  select(Math.min(n - 1, Math.max(0, base + delta)));
};
const onKeydown = event => {
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    moveSelection(1);
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    moveSelection(-1);
  } else if (event.key === 'Escape') {
    hovered.value = null;
  }
};
defineExpose({ select });
</script>

<template>
  <figure class="relative flex flex-col gap-2">
    <div class="flex items-center gap-4 text-xs text-n-slate-11">
      <span class="flex items-center gap-1.5">
        <span
          class="i-lucide-arrow-up size-3.5 text-n-teal-11"
          aria-hidden="true"
        />
        {{ gainedLabel }}
      </span>
      <span class="flex items-center gap-1.5">
        <span
          class="i-lucide-arrow-down size-3.5 text-n-iris-11"
          aria-hidden="true"
        />
        {{ lostLabel }}
      </span>
    </div>
    <svg
      :viewBox="`0 0 ${W} ${H}`"
      tabindex="0"
      class="h-auto w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
      role="img"
      :aria-label="`${gainedLabel} / ${lostLabel}`"
      @mousemove="onMove"
      @mouseleave="hovered = null"
      @keydown="onKeydown"
      @blur="hovered = null"
    >
      <line
        :x1="PAD.left"
        :x2="W - PAD.right"
        :y1="mid"
        :y2="mid"
        class="stroke-n-slate-6"
        stroke-width="1"
      />
      <text
        :x="PAD.left - 8"
        :y="PAD.top + 8"
        text-anchor="end"
        class="fill-n-slate-10 text-[10px] tabular-nums"
      >
        +{{ limit }}
      </text>
      <text
        :x="PAD.left - 8"
        :y="mid + 4"
        text-anchor="end"
        class="fill-n-slate-10 text-[10px]"
      >
        {{ 0 }}
      </text>
      <text
        :x="PAD.left - 8"
        :y="H - PAD.bottom"
        text-anchor="end"
        class="fill-n-slate-10 text-[10px] tabular-nums"
      >
        -{{ limit }}
      </text>
      <rect
        v-for="bar in gainedBars"
        :key="`g-${bar.index}`"
        data-test="bar-gained"
        :x="xAt(bar.index)"
        :y="mid - height(bar.value)"
        :width="barWidth"
        :height="height(bar.value)"
        rx="2"
        class="fill-n-teal-9"
      />
      <rect
        v-for="bar in lostBars"
        :key="`l-${bar.index}`"
        data-test="bar-lost"
        :x="xAt(bar.index)"
        :y="mid"
        :width="barWidth"
        :height="height(bar.value)"
        rx="2"
        class="fill-n-iris-9"
      />
      <rect
        v-if="hovered !== null"
        :x="xAt(hovered) - 1"
        :y="PAD.top"
        :width="slot"
        :height="H - PAD.top - PAD.bottom"
        class="fill-n-slate-12/5"
      />
    </svg>
    <figcaption
      v-if="hovered !== null"
      data-test="tooltip"
      aria-live="polite"
      class="pointer-events-none absolute right-2 top-0 flex gap-3 rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1 text-xs shadow-sm"
    >
      <span class="text-n-slate-11">{{ formatDate(dates[hovered]) }}</span>
      <span class="tabular-nums text-n-slate-12">{{
        `↑ ${formatNumber(gained[hovered])}`
      }}</span>
      <span class="tabular-nums text-n-slate-12">{{
        `↓ ${formatNumber(lost[hovered])}`
      }}</span>
    </figcaption>
  </figure>
</template>
